import { Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import HtmlEditor from '@/library/components/form/inputs/html-editor';
import Input from 'common/components/form/inputs/Input';
import MenuListSelector from 'common/components/beacon/components/MenuListSelector';
import CircledButton from 'common/components/buttons/CircledButton';
import Select from 'common/components/form/inputs/Select';
import { useFormState } from 'utils/hooks';
import { useState } from 'react';
import { selectBeaconPostSuggestions } from 'store/lists/selectors';
import { useSelector } from 'react-redux';
import SvgRender from '@/library/components/general/SvgRender';
import imageUploadIcon from '@/common/assets/svg/editor/image-upload.svg';

const Page = React.memo(({ formState, isFirst, onRemove }) => {
  const { fields, selectField } = useFormState(formState);
  const suggestions = useSelector(state =>
    selectBeaconPostSuggestions(state, 'beacon-pages-suggestions')
  );

  const getOptions = useCallback(() => {
    if (fields.url.value && !suggestions.find(s => s.value === fields.url.value)) {
      return [...suggestions, { value: fields.url.value, label: fields.url.value }];
    }

    return suggestions;
  }, [suggestions, fields.url.value]);

  return (
    <div className="d-flex align-items-center position-relative">
      <Select
        label={isFirst ? 'ASSIGN TO ORCA PAGE' : ''}
        className={`flex-1 cmb-4`}
        options={getOptions()}
        placeholder="Select page"
        isCreatable={true}
        onCreateOption={selectField('url')}
        onChange={selectField('url')}
        isValidNewOption={inp => inp?.startsWith('/')}
        {...fields.url}
      />

      <CircledButton
        type="remove"
        className="position-absolute right-0 bottom-0 mb-1 me-n3"
        onClick={onRemove}
        style={{ width: 16, height: 16 }}
      />
    </div>
  );
});

Page.propTypes = {
  formState: PropTypes.object.isRequired,
  isFirst: PropTypes.bool.isRequired,
  onRemove: PropTypes.func.isRequired
};

const Body = ({ formState }) => {
  const { fields, changeField, selectField, subStates, addSubform, removeSubform } =
    useFormState(formState);

  const [isFocused, setIsFocused] = useState(false);
  const [files, setFiles] = useState(fields.attachments?.value);

  const pages = subStates(`pages`);

  useEffect(() => {
    selectField('attachments')(files);
  }, [files, selectField]);

  return (
    <Row noGutters>
      <Col xs={3} className="pe-10">
        <Input
          label="POST TITLE"
          placeholder="Add title"
          onChange={changeField('title')}
          {...fields.title}
        />

        <MenuListSelector
          label="ASSIGN TO MENU"
          onChange={selectField('menu_id')}
          placeholder="Select menu"
          isClearable
          {...fields.menu_id}
        />

        {pages.map((formState, index) => (
          <Page
            key={index}
            formState={formState}
            index={index}
            isLast={index === pages?.length - 1}
            isFirst={index === 0}
            onRemove={() => removeSubform('pages', index)}
          />
        ))}

        <CircledButton
          type="add"
          className="text-primary fw-bold fs-12 mt-1"
          label={'Add more'}
          onClick={() => addSubform('pages')}
          style={{ width: 20, height: 20 }}
        />
      </Col>
      <Col xs={9}>
        <div className="text-violet fs-14 mb-2">
          To upload an image, press the ‘Upload image from computer’ button,{' '}
          <SvgRender
            src={imageUploadIcon}
            className="text-primary me-1 cms-4"
            style={{ width: 16, height: 15 }}
          />
          then choose an image. You can resize it by dragging the blue squares at its corners.
          Additionally, if you click on the image, you can ‘Change alternative text,’ ‘Toggle
          caption on,’ change the image's alignment, and link the image to a specific URL.
        </div>

        <HtmlEditor
          isFocused={isFocused}
          onFocus={focus =>
            focus ? setIsFocused(true) : setTimeout(() => setIsFocused(false), 100)
          }
          className="mb-0"
          placeholder="Add some text…"
          id="menu-post-content"
          minHeight={'85vh'}
          maxHeight={'85vh'}
          onChange={v => selectField('content')(v)}
          uploadedImagesGroup="posts"
          onImageUpload={files => setFiles(prev => [...(prev || []), ...files])}
          uploadImagesRequestPath="/beacon/files"
          {...fields.content}
        />
      </Col>
    </Row>
  );
};

Body.propTypes = {
  formState: PropTypes.object.isRequired
};

export default Body;
